import {subscribe, unsubscribe, publish} from './queue.js';
import {connectionCreated, connectionDeleted} from 'zobject/subpatch/operations.js';
import {hotReloadNode} from './hotReload.js';
import Connection from './cables/Connection.js';
import {
    setSelectedPadEdge,
    setInletConnections} from 'actions/index.js';
import {reader} from './reader.js';

const EMPTY = [];

window.bulkCalled = 0;
window.notBulkCalled = 0;

export default superclass => class extends superclass  {
    inlets = {};
    outlets = {};
    numInlets = 0;
    numOutlets = 0;

    // a mapping from type -> subscriber
    messageSubscribers = {};

    /**
     * registers a new inlet message type,
     * along with an action (if any) to do upon receiving.
     */
    newInlet(type, action) {
        this.inlets[type] = {action, connections: []};
        this.numInlets++;

        if (this.subSequence && this.subSequence.hasInstances) {
            this.subSequence.onChange();
        }
    }

    newOutlet(type) {
        this.outlets[type] = {connections: []};
        this.numOutlets++;

        if (this.subSequence && this.subSequence.hasInstances) {
            this.subSequence.onChange();
        }
    }

    bang() {}

    // returns true if theres atleast one inlet connected
    isInletConnected(type) {
        if (!this.inlets[type]) {
            return false;
        }
        return this.inlets[type].connections.length > 0;
    }

    isOutletConnected(type) {
        for (let i in this.getConnectedOutlets(type)) {
            return true;
        }
        return false;
    }

    getConnectedInlets(type) {
        if (this.inlets[type]) {
            return this.inlets[type].connections;
        }
        return EMPTY;
    }

    getConnectedOutlets(type) {
        if (this.outlets[type]) {
            return this.outlets[type].connections;
        }
        return EMPTY;
    }

    /** 
     * connects an outlet from some NODE ->
     * the inlet of THIS node
     */
    connectInlet(type, fromNode, fromType, edgeId, isBulk=false) {
        if (!fromNode.outlets[fromType]) {
            // if the outlet doesnt exist just use the first one
            fromType = Object.keys(fromNode.outlets)[0];
            if (!fromNode.outlets[fromType]) {
                return {};
            }
        }
        if (!this.inlets[type]) {
            return {};
        }
        let connection = new Connection(
            fromType, fromNode, type, this, this.inlets[type].action);
        if (edgeId) {
            connection.id = edgeId;
        }

        if (this.inlets[type].connections.some(
            x => x.sourceNode === fromNode && x.sourceType === fromType && x.destType === type)) {
            // dont double connect ever
            return {};
        }
        this.inlets[type].connections.push(connection);
        fromNode.outlets[fromType].connections.push(connection);

        // send a "bang" message from the source node
        // so that the destinnation node "knows about it"
        // TODO: this might be really bad. Suppose theres a + obj after
        // thenn itll try to add "BANG" to whatever number
        if (fromNode.needsStart) {
            // fromNode.bang();
        }

        if (this.needsHotReload && this.sequencerEngine && !this.sequencerEngine.isLoading) {
            if (this.args) {
                this.previousArgs = undefined;
            }
            if (fromNode.args) {
                //fromNode.previousArgs = undefined;
            }
            hotReloadNode(this);

            if (fromNode.args) {
                //fromNode.previousArgs = undefined;
            }
            // hotReloadNode(fromNode);
        }

        this.subSequence.propagate(
            connectionCreated(fromNode, this, fromType, type));

        if (!isBulk) {
        }
        return this.updateMessagingRedux(isBulk);
    }

    deleteConnection(connection) {
        let {destType, sourceType, sourceNode} = connection;
        if (this.inlets && this.inlets[destType]) {
            this.inlets[destType].connections = this.inlets[destType].connections
                .filter(c => c !== connection);
        }
        
        if (sourceNode.outlets && sourceNode.outlets[sourceType]) {
            sourceNode.outlets[sourceType].connections = sourceNode.outlets[sourceType].connections
                .filter(c => c !== connection);
            sourceNode.outlets[sourceType].connections = sourceNode.outlets[sourceType].connections
        }
            
        connection.dispose();

        this.updateMessagingRedux();

        if (this.needsHotReload) {
            if (this.args && this.previousArgs) {
                this.args = this.previousArgs;
            }
            hotReloadNode(this);
        }

        if (sourceNode.needsHotReload) {
            hotReloadNode(sourceNode);
        }

        if (this.dispatch) {
            this.dispatch(
                setSelectedPadEdge(undefined));
        }
   }

    disconnectInlet(destType, sourceNode, sourceType) {
        let connections = this.inlets[destType].connections;
        let connection = connections.find(
            c => c.sourceNode === sourceNode &&
                c.destType === destType &&
                c.sourceType === sourceType);

        this.deleteConnection(connection);

        this.subSequence.propagate(
            connectionDeleted(
                connection.sourceNode,
                connection.destNode,
                connection.sourceType,
                connection.destType));

    }

    updateMessagingRedux(isBulk=false) {
        isBulk = false;
        let obj = {};
        for (let type in this.inlets) {
            let {readers} = this.inlets[type];
            let inType = this.getFullType(type);
            obj[inType] = this.inlets[type].connections.map(
                c => c.getJson());
        }

        if (isBulk) {
            window.bulkCalled++;
            return obj;
        }
        window.notBulkCalled++;


        let subSequence = this.subSequence;
        if (this.track && this.track.subSequence) {
            subSequence = this.track.subSequence;
        }


        if (this.sequencerEngine && this.sequencerEngine.isLoading) {
            this.sequencerEngine.bulkConnections[subSequence.id] = {
                ... this.sequencerEngine.bulkConnections[subSequence.id],
                ... obj,
            };
            return;
        }

        if (subSequence) {
            let connections = subSequence.state.windows.inletConnections[subSequence.id] || {};
            subSequence.dispatch(
                setInletConnections(
                    subSequence.id,
                    {
                        ... connections,
                        ... obj
                    }));
        }
    }

    clearMessagingRedux() {
        let typesToDelete = [];
        for (let type in this.inlets) {
            let inType = this.getFullType(type);
            typesToDelete.push(inType);
        }

        let subSequence = this.subSequence;
        if (this.track && this.track.subSequence) {
            subSequence = this.track.subSequence;
        }
        if (subSequence) {
            let connections = subSequence.state.windows.inletConnections[subSequence.id] || {};
            connections = {... connections};
            for (let type of typesToDelete) {
                delete connections[type];
            }
            subSequence.dispatch(
                setInletConnections(
                    subSequence.id,
                    connections));
        }
    }

    getFullType(type, id=this.id) {
        return type + '.' + id;
    }

    sendMessage(type, ...args) {
        let outlet = this.outlets[type];
        if (!outlet) {
            return;
        }
        let connections = outlet.connections;
        for (let {destNode, action} of connections) {
            if (action) {
                action(args);
            } 
        }
        // publish(this.getFullType(type), args);
    }

    // reads a message
    receiveMessage(type) {
        if (!this.inlets[type]) {
            return EMPTY;
        }
        let {connections} = this.inlets[type];

        // try to be efficient by using the same EMPTY
        let allMsgs = EMPTY;

        // goes through all the readers connected to this inlet
        for (let connection of connections) {
            let msgs = connection.reader(); // read the messages
            if (msgs.length > 0) {
                if (allMsgs === EMPTY) {
                    allMsgs = msgs;
                } else {
                    allMsgs = [... allMsgs, ... msgs];
                }
            }
        }
        return allMsgs;
    }

    // TODO: delete and unsubribe
    // both inlets and outlets AND update redux
    disposeMessaging() {
        for (let type in this.inlets) {
            this.deleteInlet(type, false);
        }
        for (let type in this.outlets) {
            this.deleteOutlet(type, false);
        }
        this.updateMessagingRedux();

        this.inlets = undefined;
        this.outlets = undefined;
    }

    deleteInlet(type, updateRedux=true) {
        if (!this.inlets[type]) {
            return;
        }
        this.disconnectFromInlet(type);
        delete this.inlets[type];
        this.numInlets--;

        if (updateRedux) {
            this.updateMessagingRedux();
        }
    }

    disconnectFromInlet(type) {
        if (!this.inlets[type]) {
            return;
        }
        let {connections} = this.inlets[type];

        connections.forEach(c => this.deleteConnection(c));


        if (this.subSequence && this.subSequence.hasInstances ) {
            this.subSequence.onChange();
        }
    }

    disconnectFromOutlet(type) {
        if (!this.outlets[type]) {
            return;
        }
        let {connections} = this.outlets[type];

        connections.forEach(c => {
            c.sourceNode.deleteConnection(c);
        });

        if (this.subSequence && this.subSequence.hasInstances ) {
            this.subSequence.onChange();
        }
    }

    deleteOutlet(type, updateRedux=true) {
        let connections = this.outlets[type].connections;

        for (let connection of connections) {
            connection.destNode.deleteConnection(connection);
        }

        delete this.outlets[type];
        this.numOutlets--;
        if (updateRedux) {
            this.updateMessagingRedux();
        }


        if (this.subSequence && this.subSequence.hasInstances) {
            this.subSequence.onChange();
        }
    }

    getOutboundNodes() {
        return this.getAdjacentNodes(
            this.outlets, "destNode", "destType");
    }

    getAdjacentNodes(map, field, type) {
        let nodes = {};
        for (let _type in map) {
            let adj = map[_type].connections.map(
                x => ({
                    node: x[field],
                    type: x[type]
                }));
            if (adj.length > 0) {
                nodes[_type] = adj;
            }
        }
        return nodes;
    }

    getInboundNodes() {
        return this.getAdjacentNodes(
            this.inlets, "sourceNode", "sourceType");
    }

    // looks for a node based on object.name stemming from
    // a given outlet
    searchForNode(name, type, outlets=this.outlets) {
        if (!outlets) {
            return undefined;
        }
        // depth first search
        let outletKeys = Object.keys(outlets);
        let outlet = type ? outlets[type] : outlets[outletKeys[0]];
        if (this.linkedOutlet) {
            outlet = this.linkedOutlet;
        }
        if (!outlet) {
            return undefined;
        }
        let {connections} = outlet;
        for (let connection of connections) {
            let {destNode} = connection;
            if (destNode.name === name) {
                return destNode;
            }
            if (destNode.effect && destNode.effect.name === name) {
                return destNode;
            }

            let found = destNode.searchForNode(name, undefined);
            if (found) {
                return found;
            }
        }
        return undefined;
    }
}
